import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useEventTracking } from '@services/statsig';
import type { NoteCompletionStatus } from '@store/generated/notes';

import { useDebounce } from '../../../hooks/useDebounce';
import type { UserNoteTypeII } from '../../../types/types';
import { getNoteRoute } from '../../../utils/noteRouting';
import { useGetNotesListQuery } from '../store/api';
import { convertNoteListItem } from '../utils/convertNoteListItem';

export interface UseNoteQueueListOptions {
  completionStatus: NoteCompletionStatus;
  organizationId?: string;
  pageSize?: number;
  /** 'mine' scopes the queue to the caller's supervisees (supervisor role) */
  cosignScope?: 'mine';
  /** With cosignScope: include dismissed/snoozed items (show-dismissed view) */
  includeDismissed?: boolean;
}

export interface UseNoteQueueListReturn {
  notes: UserNoteTypeII[];
  isLoading: boolean;
  isFetching: boolean;
  error: boolean;
  totalCount: number;
  totalPages: number;
  page: number;
  setPage: (page: number) => void;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  navigateToNote: (note: UserNoteTypeII) => void;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  isEmpty: boolean;
  isSearchActive: boolean;
}

export function useNoteQueueList(options: UseNoteQueueListOptions): UseNoteQueueListReturn {
  const { completionStatus, organizationId, pageSize = 20, cosignScope, includeDismissed } = options;

  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTermState] = useState('');
  const debouncedSearch = useDebounce(searchTerm, 500);
  const navigate = useNavigate();
  const { note: noteMetrics } = useEventTracking();

  const { data, isLoading, isFetching, error } = useGetNotesListQuery({
    page,
    perPage: pageSize,
    organizationId: organizationId ?? undefined,
    completionStatus,
    search: debouncedSearch || undefined,
    cosignScope: cosignScope ?? undefined,
    includeDismissed: includeDismissed || undefined,
  });

  const notes: UserNoteTypeII[] = useMemo(
    () => (data?.items ?? []).map(convertNoteListItem),
    [data?.items],
  );

  const totalCount = data?.total ?? 0;
  const totalPages = Math.max(1, data?.total_pages ?? 1);

  const navigateToNote = useCallback(
    (note: UserNoteTypeII) => {
      if (note.status === 'errored') return;
      noteMetrics?.viewed?.(note.note_id, { source: 'queue' });
      const path = getNoteRoute({ note_id: note.note_id, status: note.status });
      navigate(path, { state: { origin: 'queue' } });
    },
    [navigate, noteMetrics],
  );

  const setSearchTerm = useCallback((term: string) => {
    setSearchTermState(term);
    setPage(1);
  }, []);

  return {
    notes,
    isLoading,
    isFetching,
    error: !!error,
    totalCount,
    totalPages,
    page,
    setPage,
    searchTerm,
    setSearchTerm,
    navigateToNote,
    hasNextPage: page < totalPages,
    hasPreviousPage: page > 1,
    isEmpty: !isLoading && notes.length === 0,
    isSearchActive: debouncedSearch.length > 0,
  };
}
